import React from 'react';
import { useFloatingPanels } from '../context/FloatingPanelContext';
import { FiX } from 'react-icons/fi';
import styles from '../styles/floatingPanel.module.css';

const FloatingPanelManager = () => {
  const { floatingPanels, removeFloatingPanel, updatePanelPosition } = useFloatingPanels();

  if (floatingPanels.length === 0) return null;

  // Get panel for a specific position
  const getPanelForPosition = (position) => {
    return floatingPanels.find(p => p.position === position);
  };

  // Determine grid area for each panel based on what's occupied
  const getGridStyle = (position) => {
    const count = floatingPanels.length;
    
    // Single panel - full screen
    if (count === 1) {
      return { gridColumn: '1 / -1', gridRow: '1 / -1' };
    }
    
    // Two panels
    if (count === 2) {
      const positions = floatingPanels.map(p => p.position);
      const isVerticalSplit = (positions.includes('top-left') || positions.includes('top-right')) && 
                              (positions.includes('bottom-left') || positions.includes('bottom-right'));
      const isHorizontalSplit = (positions.includes('top-left') || positions.includes('bottom-left')) && 
                                (positions.includes('top-right') || positions.includes('bottom-right'));
      
      if (isVerticalSplit) {
        // Top/bottom split
        if (position === 'top-left' || position === 'top-right') {
          return { gridColumn: '1 / -1', gridRow: '1 / 2' };
        } else {
          return { gridColumn: '1 / -1', gridRow: '2 / 3' };
        }
      } else if (isHorizontalSplit) {
        // Left/right split
        if (position === 'top-left' || position === 'bottom-left') {
          return { gridColumn: '1 / 2', gridRow: '1 / -1' };
        } else {
          return { gridColumn: '2 / 3', gridRow: '1 / -1' };
        }
      }
    }
    
    // Three or four panels - use quadrants
    const gridAreas = {
      'top-left': { gridColumn: '1 / 2', gridRow: '1 / 2' },
      'top-right': { gridColumn: '2 / 3', gridRow: '1 / 2' },
      'bottom-left': { gridColumn: '1 / 2', gridRow: '2 / 3' },
      'bottom-right': { gridColumn: '2 / 3', gridRow: '2 / 3' },
    };
    
    return gridAreas[position] || {};
  };

  const handleDragStart = (e, panelId) => {
    e.dataTransfer.setData('panelId', panelId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetPosition) => {
    e.preventDefault();
    const panelId = e.dataTransfer.getData('panelId');
    if (panelId) {
      updatePanelPosition(panelId, targetPosition);
    }
  };

  // Render a panel cell
  const renderPanelCell = (position) => {
    const panel = getPanelForPosition(position);
    const gridStyle = getGridStyle(position);
    
    if (!panel) {
      // Empty drop zone
      return (
        <div
          key={position}
          className={styles.emptyZone}
          style={gridStyle}
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, position)}
        />
      );
    }
    
    return (
      <div
        key={panel.id}
        className={styles.gridPanel}
        style={gridStyle}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, position)}
      >
        <div 
          className={styles.panelHeader}
          draggable
          onDragStart={(e) => handleDragStart(e, panel.id)}
        >
          <div className={styles.panelTitle}>{panel.title}</div>
          <div className={styles.panelActions}>
            <button
              className={styles.panelButton}
              onClick={() => removeFloatingPanel(panel.id)}
            >
              <FiX size={14} />
            </button>
          </div>
        </div>
        <div className={styles.panelContent}>
          {panel.content}
        </div>
      </div>
    );
  };

  // Get all positions that should be rendered
  const positionsToRender = ['top-left', 'top-right', 'bottom-left', 'bottom-right']
    .filter(pos => floatingPanels.some(p => p.position === pos));

  return (
    <div className={styles.gridContainer}>
      {positionsToRender.map(pos => renderPanelCell(pos))}
    </div>
  );
};

export default FloatingPanelManager;
